import { BCMSRecord, BCMSStateManagerCRUD, DisabledPrompt, useBCMSContext, useStateManager } from "@bcm-dev/bcms-vite";
import CustomSelect, { CustomSelectItem } from "components/custom-ui/CustomSelect";
import {
	CenterContentContainer,
	ContentHeaderView,
	FormContainer,
	FormRowView,
	PageContainer,
	PageContentView,
	TableBody,
	TableColumnContainer, TableColumnView,
	TableItemContainer,
	TableItemTextView, TableItemView,
	TableView
} from "components/shared";
import { PrimaryButton, SecondaryButton, UICheckbox } from "components/ui";
import Classes, { Account } from "model";
import DownloadTransactionsFilter from "./classes/DownloadTransactionsFilter";
import "./styles.css";
import "./mobile.css";


/**
 * @function DownloadTransactionsPage 
 * @returns  {React.Component}
 * @exported true
 * @description The main page component for "Download Transactions".
 */
export default function DownloadTransactionsPage(props) {
  const globalController = useBCMSContext();
  BCMSRecord.globalControllerFor(Classes, globalController);

  const data = { ...getDefaultData(), ...props.data ?? {} };
  const ui = { ...getDefaultUI(), ...props.ui ?? {} };

  const controller = (state, setState) => ({
      data: state.data,
      ui: state.ui,

      onChange: prop => value => {
        BCMSStateManagerCRUD.init(state)
        .update([ "data", prop ], value)
        .submit(setState);
      },

      onFilterChange: prop => value => {
        BCMSStateManagerCRUD.init(state)
        .update([ "data", "filter", prop ], value)
        .submit(setState);
      },

      // On account row checkbox click, add the selected account to the 
      // filter objected and update state data
      // indicating that this account has been selected.
      onAccountChange: accountID => value => {
        let currSelectedAccountIDs = state.data.filter.selectedAccountIDs;
        currSelectedAccountIDs = currSelectedAccountIDs ?? [];

        const accounts = state.data.accounts.reduce((acc, item) => {
          if(item.id === accountID) {
            item.isChecked = value === true ? "true" : "false";
          }
          acc.push(item);
          return acc;
        }, []);

        if(value === true) {
          currSelectedAccountIDs.push(accountID);
        } else {
          currSelectedAccountIDs = currSelectedAccountIDs.filter(id => id !== accountID);

          if(currSelectedAccountIDs.length === 0) {
            currSelectedAccountIDs = undefined;
          }
        }

        BCMSStateManagerCRUD.init(state)  
        .update([ "data", "filter", "selectedAccountIDs" ], currSelectedAccountIDs)
        .update([ "data", "accounts" ], accounts)
        .submit(setState);
      },
      
        onFilterReset: evt => {
          const filter = data.filter.reset();
          const resetAccounts = state.data.accounts.reduce((acc, item) => {
            item.isChecked = "false";
            acc.push(item);
            return acc;
          }, []);

          BCMSStateManagerCRUD.init(state)  
          .update([ "data", "filter" ], filter)
          .update([ "data", "accounts" ], resetAccounts)
          .submit(setState);
        },
  });
  
  return(
    <PageContainer className="download-transactions-page" state={ { data, ui } } controller={ controller }>
      <PageContent/>
    </PageContainer>
  );
}

export const getDefaultData = () => ({
  accounts: Account.initAll().reduce((acc, item) => {
    item.isChecked = "false";
    acc.push(item);
    return acc;
  }, []),
  filter: DownloadTransactionsFilter.init()
  // Example of setting filter values.
  /*filter: DownloadTransactionsFilter.init({
    date: "365",
    type: "check",
    exportFormat: "csv",
    selectedAccountIDs: ["0", "1"]
  })*/
});
export const getDefaultUI = () => ({
  // no ui changes for now
  dateSelect: { isActive: false },
  typeSelect: { isActive: false },
  exportFormatSelect: { isActive: false }
});

/**
 * @function PageContent
 * @returns {React.Component}
 * @exported false
 * @description Content for the page. Created in order to use hook "useStateManager".
 */
function PageContent(props) {
  const { data, ui, onFilterChange, onFilterReset } = useStateManager();
  const filter = data.filter ?? {};
  const accountRows = data.accounts.reduce((acc, account) => {
    acc.push(<DesktopTableRow key={ account.id } account= { account } />);
    return acc;
  }, []);

  console.log(data.accounts)

  return (
    <PageContentView>
    <CenterContentContainer>
      <ContentHeaderView text="Download Transactions" />
      <FormContainer className="small-form-width">
        <div className="search-options-dropdown">
          <FormRowView text="Transaction Date" className="company-select-row">
            <CustomSelect active={ ui.dateSelect.isActive } value={ filter.date } onChange={ onFilterChange("date") }>
              <CustomSelectItem value="30">Last 30 Days</CustomSelectItem>
              <CustomSelectItem value="60">Last 60 Days</CustomSelectItem>
              <CustomSelectItem value="60">Last 90 Days</CustomSelectItem>
              <CustomSelectItem value="120">Last 120 Days</CustomSelectItem>
              <CustomSelectItem value="365">Last One Year</CustomSelectItem>
              <CustomSelectItem value="730">Last Two Years</CustomSelectItem>
            </CustomSelect>
          </FormRowView>
          <FormRowView text="Transaction Type" className="transaction-type-select-row">
            <CustomSelect active={ ui.typeSelect.isActive } value={ filter.type } 
            onChange={ onFilterChange("type") }>
                <CustomSelectItem value="all">All</CustomSelectItem>
                <CustomSelectItem value="debit">Debit</CustomSelectItem>
                <CustomSelectItem value="credit">Credit</CustomSelectItem>
                <CustomSelectItem value="check">Check</CustomSelectItem>
            </CustomSelect>
          </FormRowView>
          <FormRowView text="Export Format" className="offset-account-select-row">
            <CustomSelect active={ ui.exportFormatSelect.isActive } value={ filter.exportFormat } onChange={ onFilterChange("exportFormat") }>
              <CustomSelectItem value="csv">Comma Separated File (.csv)</CustomSelectItem>
            </CustomSelect>
          </FormRowView>
        </div>
      </FormContainer>
      <p className="select-accounts-label">Select Accounts</p>

        <TableView className="download-transactions-table">
          <TableColumnContainer>
            <TableColumnView className="check-box-header" />
            <TableColumnView className="nickname-header" text="Nickname" />
            <TableColumnView text="Account Type" />
            <TableColumnView text="Account Number" />
          </TableColumnContainer>
          <TableBody>
            { accountRows }
          </TableBody>
        </TableView>
      <p>Note: Please select no more than 25 accounts to download at one time.</p>
      <div className="flex-container flex-horizontal align-center button-container ">
        <SecondaryButton text="Clear" onClick={ onFilterReset } />
        <DisabledPrompt>
          <PrimaryButton text="Download" />
        </DisabledPrompt>
      </div>
    </CenterContentContainer>
  </PageContentView>
  );
}

/**
 * @function DesktopTableRow
 * @returns {React.Component}
 * @exported false
 * @description A desktop table row used in a table in the page.
 * @param {Account} account - Sets the account for the row.
 * @example
 * const accountRows = data.accounts.reduce((acc, account) => {
    acc.push(<DesktopTableRow key={ account.id } account= { account } />);
    return acc;
  }, []);

   <TableBody>
      { accountRows }
    </TableBody>
 */
function DesktopTableRow(props) {
  const { onAccountChange } = useStateManager();
  const account = props.account ?? {};
  let type = account.type;
  if(account.type === "cod") type = "certificate";
  
  return(
    <TableItemContainer>
      <TableItemView className="row-checkbox">
        <UICheckbox value={ account.isChecked == "true" } onChange={ onAccountChange(account.id) } />
      </TableItemView>
      <TableItemTextView 
        text={
          <DisabledPrompt>
              <p>{ account.nickname }</p>
          </DisabledPrompt>
        } 
      />
      <TableItemTextView 
        className="account-type"
        text={ type } 
      />
      <TableItemTextView 
        text={ account.number } 
      />
    </TableItemContainer>
  );
}